"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";
import { BattleHPBar } from "@/components/game/BattleHPBar";

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const GOLD = "#c5a83a";
const RESTED = "#4a9c3e"; // healthy / full HP
const FAINTED = "#b04848"; // fainted / KO
const BACKDROP = "rgba(26, 26, 26, 0.7)";

// Heal animation stage timings (ms)
const STAGE1_END = 600; // counter / loading bar fill
const STAGE2_PER_MEMBER = 360; // each Surggie's HP bar refills (also the bar's tween length)
const STAGE3_HOLD = 800; // confirmation text dwell before handoff

type HealStage = "idle" | "stage1" | "stage2" | "stage3" | "done";

export type SurggieCenterMember = {
  speciesId: string;
  nickname?: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
  fainted: boolean;
};

export type SurggieCenterProps = {
  /** Party to display (read-only). */
  party: SurggieCenterMember[];
  /** Called once the heal animation finishes. The PARENT performs the real heal
   *  (setting hpCurrent=hpMax, fainted=false); this component is purely visual. */
  onHealComplete: () => void;
  /** Player declines / leaves without healing. */
  onLeave: () => void;
};

/** Sprite path for a party member keyed by speciesId.
 *  Matches the convention in InventoryScreen (char_<speciesId>.png) with a
 *  fallback to the numbered overworld sprite used by BattleSprite. */
function spriteSrc(speciesId: string): string {
  return `/assets/surggies/char_${speciesId}.png`;
}

function spriteFallbackSrc(speciesId: string): string {
  return `/assets/surggies/char_${speciesId}_1.png`;
}

function MemberSprite({ speciesId, revived }: { speciesId: string; revived: boolean }) {
  const [src, setSrc] = useState<string>(() => spriteSrc(speciesId));
  const [triedFallback, setTriedFallback] = useState(false);

  useEffect(() => {
    setSrc(spriteSrc(speciesId));
    setTriedFallback(false);
  }, [speciesId]);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      width={32}
      height={32}
      draggable={false}
      onError={(e) => {
        if (!triedFallback) {
          setTriedFallback(true);
          setSrc(spriteFallbackSrc(speciesId));
        } else {
          // Both sprite paths failed — hide the broken-image glyph.
          (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
        }
      }}
      style={{
        imageRendering: "pixelated",
        flexShrink: 0,
        // Fainted Surggies are greyed out, then snap back to full color when revived.
        filter: revived ? "none" : "grayscale(0.8) brightness(0.75)",
        transition: "filter 0.3s steps(3)",
      }}
    />
  );
}

function PixelButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="relative font-mono font-bold uppercase tracking-widest"
      style={{
        backgroundColor: active && !disabled ? INK : CREAM,
        color: active && !disabled ? CREAM : INK,
        border: `2px solid ${INK}`,
        padding: "0.5rem 0.75rem 0.5rem 1.5rem",
        fontSize: "0.85rem",
        lineHeight: 1,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "none",
        imageRendering: "pixelated",
        textAlign: "left",
      }}
    >
      {active && !disabled && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: "0.4rem",
            top: "50%",
            transform: "translateY(-50%)",
            color: CREAM,
            fontSize: "0.85rem",
            lineHeight: 1,
          }}
        >
          &#9654;
        </span>
      )}
      {children}
    </button>
  );
}

/** Inline toast shown after healing — cream bg, gold border, 3s auto-dismiss */
function HealToast({ visible, onDismiss }: { visible: boolean; onDismiss: () => void }) {
  const [phase, setPhase] = useState<"enter" | "visible" | "exit" | null>(null);
  const SLIDE_MS = 250;

  useEffect(() => {
    if (!visible) {
      setPhase(null);
      return;
    }
    setPhase("enter");
    const raf = requestAnimationFrame(() => setPhase("visible"));
    const hide = window.setTimeout(() => setPhase("exit"), 3000);
    const gone = window.setTimeout(() => onDismiss(), 3000 + SLIDE_MS);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(hide);
      window.clearTimeout(gone);
    };
  }, [visible, onDismiss]);

  if (!visible || phase === null) return null;
  const offscreen = phase === "enter" || phase === "exit";

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        width: 320,
        zIndex: 9999,
        display: "flex",
        backgroundColor: CREAM,
        border: `2px solid ${GOLD}`,
        boxShadow: `2px 2px 0 ${INK}`,
        imageRendering: "pixelated",
        transform: offscreen ? "translateX(120%)" : "translateX(0)",
        transition: `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        cursor: "pointer",
      }}
      onClick={() => {
        setPhase("exit");
        window.setTimeout(onDismiss, SLIDE_MS);
      }}
    >
      <div aria-hidden style={{ width: 4, backgroundColor: GOLD, flexShrink: 0 }} />
      <div style={{ flex: 1, padding: "10px 12px" }}>
        <PixelText
          as="div"
          size="md"
          glow="none"
          style={{ color: INK, fontWeight: 700, fontSize: 13, letterSpacing: "0.05em" }}
        >
          Team Fully Rested!
        </PixelText>
        <PixelText
          as="div"
          size="sm"
          glow="none"
          style={{ color: INK, fontSize: 11, fontWeight: 400, textTransform: "none", marginTop: 2 }}
        >
          {"Don't forget to save."}
        </PixelText>
      </div>
    </div>
  );
}

/** Pixel-art loading bar — green fill on ink background, fills over stage 1 */
function LoadingBar({ filling }: { filling: boolean }) {
  return (
    <div
      aria-hidden
      style={{
        width: "100%",
        height: 10,
        backgroundColor: INK,
        imageRendering: "pixelated",
        overflow: "hidden",
        border: `2px solid ${INK}`,
      }}
    >
      <div
        style={{
          height: "100%",
          width: filling ? "100%" : "0%",
          backgroundColor: RESTED,
          transition: `width ${STAGE1_END}ms steps(8, end)`,
        }}
      />
    </div>
  );
}

/** The nurse-at-the-counter scene. Pure decoration — the heart of the pixel aesthetic. */
function CounterScene({ pulsing }: { pulsing: boolean }) {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 16,
        border: `2px solid ${INK}`,
        backgroundColor: "#efe4c4",
        padding: "1rem 1rem 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Healing machine — pixel capsules that pulse while restoring */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 12,
                height: 12,
                backgroundColor: pulsing ? RESTED : INK,
                border: `2px solid ${INK}`,
                animation: pulsing ? `surggieHealPulse 0.6s steps(2) ${i * 0.15}s infinite` : "none",
              }}
            />
          ))}
        </div>
        <div style={{ width: 56, height: 6, backgroundColor: INK }} />
        <div style={{ width: 64, height: 10, backgroundColor: "#9b8a5a", border: `2px solid ${INK}` }} />
      </div>

      {/* Nurse — built from pixel blocks (no asset dependency) */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* hat with cross */}
        <div style={{ position: "relative", width: 26, height: 12, backgroundColor: CREAM, border: `2px solid ${INK}` }}>
          <div style={{ position: "absolute", left: 11, top: 2, width: 4, height: 8, backgroundColor: FAINTED }} />
          <div style={{ position: "absolute", left: 7, top: 4, width: 12, height: 4, backgroundColor: FAINTED }} />
        </div>
        {/* head */}
        <div style={{ width: 22, height: 20, backgroundColor: "#e8c39a", border: `2px solid ${INK}`, borderTop: "none" }} />
        {/* body */}
        <div style={{ width: 34, height: 22, backgroundColor: GOLD, border: `2px solid ${INK}`, borderTop: "none" }} />
      </div>

      {/* counter */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 12,
          backgroundColor: "#7a6a3e",
          borderTop: `2px solid ${INK}`,
        }}
      />
      <style>{`
        @keyframes surggieHealPulse {
          0% { background-color: ${INK}; }
          50% { background-color: ${RESTED}; }
          100% { background-color: ${INK}; }
        }
      `}</style>
    </div>
  );
}

export function SurggieCenter({ party, onHealComplete, onLeave }: SurggieCenterProps) {
  const [healStage, setHealStage] = useState<HealStage>("idle");
  // How many members have begun their HP refill (stage 2 staggers this 0..party.length).
  const [healedCount, setHealedCount] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const healingRef = useRef(false);

  const healing = healStage !== "idle" && healStage !== "done";

  const needsCare = useMemo(
    () => party.some((m) => m.fainted || m.hpCurrent < m.hpMax),
    [party],
  );

  // Reset the visual heal counter whenever the party changes while idle.
  useEffect(() => {
    if (healStage === "idle") setHealedCount(0);
  }, [party, healStage]);

  const handleHeal = useCallback(async () => {
    if (healingRef.current || healStage !== "idle") return;
    healingRef.current = true;

    // Stage 1: place capsules on the machine, loading bar fills.
    setHealStage("stage1");
    setHealedCount(0);
    await new Promise((r) => setTimeout(r, STAGE1_END));

    // Stage 2: refill each Surggie's HP bar to full, one at a time. Bumping
    // healedCount flips that member's HP bar target to hpMax (the bar tweens it)
    // and revives any fainted member.
    setHealStage("stage2");
    for (let i = 0; i < party.length; i++) {
      setHealedCount(i + 1);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, STAGE2_PER_MEMBER));
    }

    // Stage 3: confirmation jingle-moment.
    setHealStage("stage3");
    await new Promise((r) => setTimeout(r, STAGE3_HOLD));

    setHealStage("done");
    setShowToast(true);
    healingRef.current = false;

    // Hand control back to the shell, which performs the real heal + close.
    onHealComplete();
  }, [healStage, party, onHealComplete]);

  // Keyboard handling: Z/Enter confirms, X/Esc leaves.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const blocked = new Set(["Enter", "z", "Z", "x", "X", "Escape"]);
      if (blocked.has(key)) e.preventDefault();
      if (healing) return;
      if (key === "x" || key === "X" || key === "Escape") {
        onLeave();
        return;
      }
      if (key === "Enter" || key === "z" || key === "Z") {
        void handleHeal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [healing, handleHeal, onLeave]);

  return (
    <>
      <HealToast visible={showToast} onDismiss={() => setShowToast(false)} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Surggie Center"
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: BACKDROP, imageRendering: "pixelated" }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !healing) onLeave();
        }}
      >
        <div
          className="flex flex-col"
          style={{
            backgroundColor: CREAM,
            border: `2px solid ${INK}`,
            width: "min(560px, 92vw)",
            maxHeight: "88vh",
            imageRendering: "pixelated",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between"
            style={{ borderBottom: `2px solid ${INK}`, padding: "0.75rem 1rem", backgroundColor: INK }}
          >
            <PixelText as="div" glow="none" size="md" style={{ color: CREAM, letterSpacing: "0.15em" }}>
              Surggie Center
            </PixelText>
            <button
              type="button"
              onClick={() => {
                if (!healing) onLeave();
              }}
              aria-label="Close"
              disabled={healing}
              className="font-mono font-bold"
              style={{
                backgroundColor: INK,
                color: CREAM,
                border: "none",
                fontSize: "1rem",
                lineHeight: 1,
                opacity: healing ? 0.4 : 1,
                cursor: healing ? "not-allowed" : "pointer",
                transition: "none",
              }}
            >
              X
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto" style={{ padding: "1.25rem", minHeight: "320px", color: INK }}>
            <div className="flex flex-col gap-4">
              <CounterScene pulsing={healStage === "stage1" || healStage === "stage2"} />

              {/* Stage 1: loading bar */}
              {healStage === "stage1" && (
                <div className="flex flex-col gap-2">
                  <PixelText
                    as="div"
                    glow="none"
                    size="md"
                    className="!text-[#1a1a1a] !tracking-normal !normal-case"
                  >
                    Restoring your Surggies...
                  </PixelText>
                  <LoadingBar filling />
                </div>
              )}

              {/* Nurse dialog */}
              <div
                style={{
                  border: `2px solid ${INK}`,
                  padding: "0.875rem 1rem",
                  backgroundColor: CREAM,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {healStage === "idle" && (
                  <>
                    <PixelText
                      as="div"
                      glow="none"
                      size="md"
                      className="!text-[#1a1a1a] !tracking-normal !normal-case"
                    >
                      Welcome to the Surggie Center!
                    </PixelText>
                    <PixelText
                      as="div"
                      glow="none"
                      size="md"
                      className="!text-[#1a1a1a] !tracking-normal !normal-case"
                    >
                      {needsCare
                        ? "Shall I restore your Surggies to full health?"
                        : "Your Surggies are already at full health! Shall I check them over anyway?"}
                    </PixelText>
                  </>
                )}
                {(healStage === "stage2" || healStage === "stage3") && (
                  <PixelText
                    as="div"
                    glow="none"
                    size="md"
                    className="!text-[#1a1a1a] !tracking-normal !normal-case"
                  >
                    {healStage === "stage3"
                      ? "Your team is fully rested! ♥"
                      : "Just a moment..."}
                  </PixelText>
                )}
                {healStage === "done" && (
                  <PixelText
                    as="div"
                    glow="none"
                    size="md"
                    className="!text-[#1a1a1a] !tracking-normal !normal-case"
                  >
                    Your Surggies are rested and ready. Take care out there!
                  </PixelText>
                )}
              </div>

              {/* Party roster with HP bars */}
              {party.length === 0 ? (
                <PixelText
                  as="div"
                  glow="none"
                  size="sm"
                  className="!text-[#1a1a1a] !tracking-normal !normal-case"
                >
                  No Surggies in your party.
                </PixelText>
              ) : (
                <div className="flex flex-col gap-2">
                  {party.map((m, i) => {
                    const displayName = m.nickname?.trim() || m.speciesId;
                    // During stage 2+ once this member's turn arrives, its bar
                    // targets full HP (BattleHPBar tweens current -> hpMax).
                    const refilled = healedCount > i;
                    const shownHp = refilled ? m.hpMax : m.hpCurrent;
                    const isDown = m.fainted && !refilled;
                    return (
                      <div
                        key={`${m.speciesId}-${i}`}
                        className="flex items-center gap-3"
                        style={{ border: `2px solid ${INK}`, padding: "0.5rem 0.75rem" }}
                      >
                        <MemberSprite speciesId={m.speciesId} revived={!isDown} />
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <PixelText
                              as="span"
                              glow="none"
                              size="sm"
                              className="!text-[#1a1a1a] !tracking-normal !normal-case truncate"
                            >
                              {displayName} &middot; Lv{m.level}
                            </PixelText>
                            {isDown ? (
                              <PixelText
                                as="span"
                                glow="none"
                                size="sm"
                                className="!tracking-widest"
                                style={{ color: FAINTED }}
                              >
                                FAINTED
                              </PixelText>
                            ) : null}
                          </div>
                          <BattleHPBar
                            current={shownHp}
                            max={m.hpMax}
                            width={220}
                            height={8}
                            showNumeric
                            tweenMs={STAGE2_PER_MEMBER}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Buttons */}
              {healStage === "idle" && (
                <div className="flex justify-end gap-2">
                  <PixelButton active={false} onClick={onLeave}>
                    Not right now
                  </PixelButton>
                  <PixelButton active disabled={party.length === 0} onClick={() => void handleHeal()}>
                    Yes please!
                  </PixelButton>
                </div>
              )}
              {(healStage === "stage1" || healStage === "stage2" || healStage === "stage3") && (
                <div className="flex justify-end">
                  <PixelButton active={false} disabled onClick={() => undefined}>
                    Restoring...
                  </PixelButton>
                </div>
              )}
            </div>
          </div>

          {/* Footer hints */}
          <div
            className="flex items-center justify-between"
            style={{ borderTop: `2px solid ${INK}`, padding: "0.5rem 1rem", backgroundColor: CREAM }}
          >
            <PixelText
              as="div"
              glow="none"
              size="sm"
              className="!text-[#1a1a1a] !tracking-normal !normal-case"
            >
              {healing ? "Please wait..." : "Z / Enter — Heal"}
            </PixelText>
            <PixelText
              as="div"
              glow="none"
              size="sm"
              className="!text-[#1a1a1a] !tracking-normal !normal-case"
            >
              X / Esc — Leave
            </PixelText>
          </div>
        </div>
      </div>
    </>
  );
}

export default SurggieCenter;
